import React, { useCallback, useEffect, useState } from "react";
import { Patient } from "../../PatientCard";
import LabRequisitionModal from "./LabRequisitionModal";
import LabRequisitionHistory from "./LabRequisitionHistory";
import LabRequisitionService, {
  LabRequisition,
} from "../../../../services/labRequisitionService";

interface PatientLabOrdersTabProps {
  patient: Patient;
  onDataUpdate?: (tabName: string) => void;
  refreshTrigger?: number;
}

const PatientLabOrdersTab: React.FC<PatientLabOrdersTabProps> = ({
  patient,
}) => {
  const [showLabModal, setShowLabModal] = useState(false);
  const [showLabHistory, setShowLabHistory] = useState(false);
  const [labRequisitions, setLabRequisitions] = useState<LabRequisition[]>([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [statsLoading, setStatsLoading] = useState(false);

  const handleRequisitionCreated = (requisitionId: number) => {
    // alert("Lab requisition created successfully!");
    setShowLabModal(false);
    setRefreshTrigger((prev) => prev + 1);
    loadLabRequisitions();
  };

  const loadLabRequisitions = useCallback(async () => {
    if (!patient.id) return;

    setStatsLoading(true);
    try {
      const requisitions =
        await LabRequisitionService.getPatientLabRequisitions(patient.id);
      setLabRequisitions(requisitions);
    } catch (error) {
      console.error("Failed to load lab requisitions for stats:", error);
    } finally {
      setStatsLoading(false);
    }
  }, [patient.id]);

  useEffect(() => {
    loadLabRequisitions();
  }, [loadLabRequisitions]);

  const calculateStats = () => {
    const pendingOrders = labRequisitions.filter(
      (req) => req.status === "draft" || req.status === "issued"
    );
    const completedTests = labRequisitions.filter(
      (req) => req.status === "completed"
    );
    const urgentOrders = labRequisitions.filter(
      (req) => req.priority === "urgent" || req.priority === "stat"
    );

    const lastTestDate =
      labRequisitions.length > 0
        ? labRequisitions.reduce(
            (latest, req) =>
              !latest || new Date(req.requisition_date) > new Date(latest)
                ? req.requisition_date
                : latest,
            null as string | null
          )
        : null;

    return {
      pendingOrders: pendingOrders.length,
      completedTests: completedTests.length,
      urgentOrders: urgentOrders.length,
      lastTestDate: lastTestDate
        ? LabRequisitionService.formatDate(lastTestDate)
        : "--",
    };
  };

  const handleGeneratePDF = (requisitionId: number) => {
    // PDF generation logic handled in the history component
  };

  const buttonStyle = (
    variant: "primary" | "secondary" = "primary"
  ): React.CSSProperties => ({
    padding: "12px 24px",
    borderRadius: "8px",
    border: "none",
    fontSize: "14px",
    fontWeight: "500",
    cursor: "pointer",
    transition: "all 0.2s ease",
    background: variant === "primary" ? "#3b82f6" : "#e5e7eb",
    color: variant === "primary" ? "white" : "#374151",
    display: "flex",
    alignItems: "center",
    gap: "8px",
  });

  return (
    <>
      <div className="lab-orders-container">
        {/* Header with Action Buttons */}
        <div className="lab-orders-header">
          <div className="header-info">
            <h3>Lab Orders & Results</h3>
            <p>
              Order lab tests and view results for{" "}
              {patient.full_name ||
                `${patient.first_name} ${patient.last_name}`}
            </p>
          </div>

          <div className="header-actions">
            <button
              className="primary-button"
              onClick={() => setShowLabModal(true)}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "#2563eb")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "#3b82f6")
              }
            >
              <span className="button-icon">+</span>
              <span className="button-text">New Lab Order</span>
            </button>
          </div>
        </div>

        {/* Patient Alerts for Lab Orders */}
        {(patient.allergies?.length || patient.chronic_conditions?.length) && (
          <div className="patient-alerts">
            <h4 className="alert-title">Important Medical Information</h4>
            {patient.allergies?.length && (
              <div className="alert-item">
                <strong>Allergies:</strong> {patient.allergies.join(", ")}
              </div>
            )}
            {patient.chronic_conditions?.length && (
              <div className="alert-item">
                <strong>Chronic Conditions:</strong>{" "}
                {patient.chronic_conditions.join(", ")}
              </div>
            )}
          </div>
        )}

        {/* Lab Requisition History - Inline */}
        <LabRequisitionHistory
          patient={patient}
          isOpen={true}
          onClose={() => {}}
          refreshTrigger={refreshTrigger}
          onGeneratePDF={handleGeneratePDF}
        />

        {/* Lab Statistics */}
        <div className="lab-statistics">
          <h4 className="stats-title">Lab Activity Summary</h4>

          {statsLoading ? (
            <div className="stats-loading">
              <div className="spinner" />
            </div>
          ) : (
            <div className="stats-grid">
              {(() => {
                const stats = calculateStats();
                return (
                  <>
                    <div className="stat-item">
                      <div className="stat-value pending">
                        {stats.pendingOrders}
                      </div>
                      <div className="stat-label">Pending Orders</div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-value completed">
                        {stats.completedTests}
                      </div>
                      <div className="stat-label">Completed Tests</div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-value urgent">
                        {stats.urgentOrders}
                      </div>
                      <div className="stat-label">Urgent Orders</div>
                    </div>

                    <div className="stat-item">
                      <div className="stat-value neutral">
                        {stats.lastTestDate}
                      </div>
                      <div className="stat-label">Last Test Date</div>
                    </div>
                  </>
                );
              })()}
            </div>
          )}
        </div>

        {/* Information Panel */}
        {/* <div className="info-panel">
          <div className="info-icon">🔬</div>
          <h4 className="info-title">Complete Lab Management System</h4>
          <p className="info-description">
            Order lab tests, track results, generate requisition forms, and
            manage the complete laboratory workflow. Integration with external
            labs and automated result notifications.
          </p>
          <div className="info-features">
            <div className="feature-item">Test Ordering & Tracking</div>
            <div className="feature-item">PDF Requisition Generation</div>
            <div className="feature-item">External Lab Integration</div>
            <div className="feature-item">Result Management</div>
            <div className="feature-item">Patient Instructions</div>
            <div className="feature-item">Cost Estimation</div>
          </div>
        </div> */}
      </div>

      {/* Lab Requisition Modal */}
      <LabRequisitionModal
        patient={patient}
        isOpen={showLabModal}
        onClose={() => setShowLabModal(false)}
        onRequisitionCreated={handleRequisitionCreated}
      />

      <style>
        {`
        .lab-orders-container {
          padding: 16px;
          height: 100%;
          overflow: auto;
        }

        .lab-orders-header {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-bottom: 24px;
          padding: 20px;
          background: #ffffff;
          border-radius: 12px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
        }

        .header-info h3 {
          font-size: 18px;
          font-weight: 600;
          margin: 0 0 4px 0;
          color: #1f2937;
        }

        .header-info p {
          font-size: 13px;
          color: #6b7280;
          margin: 0;
        }

        .header-actions {
          display: flex;
          justify-content: center;
        }

        .primary-button {
          padding: 12px 20px;
          border-radius: 8px;
          border: none;
          font-size: 14px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s ease;
          background: #3b82f6;
          color: white;
          display: flex;
          align-items: center;
          gap: 8px;
          width: 100%;
          justify-content: center;
        }

        .button-icon {
          font-size: 16px;
          font-weight: bold;
        }

        .button-text {
          white-space: nowrap;
        }

        .patient-alerts {
          background: #fef3c7;
          border: 1px solid #f59e0b;
          border-radius: 8px;
          padding: 16px;
          margin-bottom: 24px;
        }

        .alert-title {
          color: #92400e;
          font-size: 14px;
          font-weight: 600;
          margin-bottom: 8px;
        }

        .alert-item {
          color: #92400e;
          font-size: 13px;
          margin-bottom: 4px;
        }

        .alert-item:last-child {
          margin-bottom: 0;
        }

        .lab-statistics {
          background: #ffffff;
          border-radius: 12px;
          padding: 20px;
          box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
          border: 1px solid #e5e7eb;
          margin-bottom: 24px;
        }

        .stats-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 16px;
          color: #1f2937;
        }

        .stats-loading {
          text-align: center;
          padding: 20px;
        }

        .spinner {
          width: 20px;
          height: 20px;
          border: 2px solid #e2e8f0;
          border-top: 2px solid #3b82f6;
          border-radius: 50%;
          animation: spin 1s linear infinite;
          margin: 0 auto;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .stat-item {
          text-align: center;
        }

        .stat-value {
          font-size: 24px;
          font-weight: 700;
          margin-bottom: 4px;
        }

        .stat-value.pending {
          color: #3b82f6;
        }

        .stat-value.completed {
          color: #10b981;
        }

        .stat-value.urgent {
          color: #f59e0b;
        }

        .stat-value.neutral {
          color: #6b7280;
        }

        .stat-label {
          font-size: 12px;
          color: #6b7280;
        }

        .info-panel {
          background: #f0f9ff;
          border: 1px solid #dbeafe;
          border-radius: 8px;
          padding: 16px;
          text-align: center;
        }

        .info-icon {
          font-size: 20px;
          margin-bottom: 8px;
        }

        .info-title {
          font-size: 16px;
          font-weight: 600;
          margin-bottom: 8px;
          color: #1e40af;
        }

        .info-description {
          font-size: 14px;
          color: #1e40af;
          margin-bottom: 16px;
          line-height: 1.5;
        }

        .info-features {
          display: grid;
          grid-template-columns: 1fr;
          gap: 8px;
          font-size: 12px;
          color: #1e40af;
        }

        .feature-item {
          position: relative;
          padding-left: 16px;
        }

        .feature-item:before {
          content: '✓';
          position: absolute;
          left: 0;
          color: #10b981;
          font-weight: bold;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        /* Tablet styles */
        @media (min-width: 640px) {
          .lab-orders-container {
            padding: 24px;
          }

          .lab-orders-header {
            flex-direction: row;
            justify-content: space-between;
            align-items: flex-start;
          }

          .header-info h3 {
            font-size: 20px;
          }

          .header-info p {
            font-size: 14px;
          }

          .header-actions {
            justify-content: flex-end;
          }

          .primary-button {
            width: auto;
            min-width: 160px;
          }

          .stats-grid {
            grid-template-columns: repeat(4, 1fr);
          }

          .info-features {
            grid-template-columns: repeat(2, 1fr);
            gap: 12px;
          }
        }

        /* Desktop styles */
        @media (min-width: 1024px) {
          .info-features {
            grid-template-columns: repeat(3, 1fr);
          }
        }

        .primary-button:hover:not(:disabled) {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
      `}
      </style>
    </>
  );
};
export default PatientLabOrdersTab;
